export interface SizeUnit { label: string; factor: number; binary: boolean }

const DEC = ["B", "KB", "MB", "GB", "TB", "PB"];
const BIN = ["B", "KiB", "MiB", "GiB", "TiB", "PiB"];

export const DECIMAL_UNITS: SizeUnit[] = DEC.map((label, i) => ({ label, factor: 1000 ** i, binary: false }));
export const BINARY_UNITS: SizeUnit[] = BIN.map((label, i) => ({ label, factor: 1024 ** i, binary: true }));
export const ALL_UNITS = [...DECIMAL_UNITS, ...BINARY_UNITS.slice(1)];

export const toBytes = (value: number, unit: string): number => value * (ALL_UNITS.find(u => u.label === unit)?.factor ?? 1);
export const fromBytes = (bytes: number, unit: SizeUnit): number => bytes / unit.factor;

/** What an operating system that counts in 1024s shows for a drive sold in decimal units (e.g. 500 GB -> 465.66 GiB). */
export function advertisedToShown(value: number, unit: "GB" | "TB") {
  const bytes = toBytes(value, unit);
  const gib = bytes / 1024 ** 3;
  // The gap is the ratio of the binary unit the OS shows to the decimal unit on the box, for the unit chosen.
  const lostPct = unit === "TB" ? (1 - 1000 ** 4 / 1024 ** 4) * 100 : (1 - 1000 ** 3 / 1024 ** 3) * 100;
  return { gib, tib: bytes / 1024 ** 4, lostPct };
}

export function formatNumber(n: number): string {
  if (!isFinite(n)) return "—";
  if (n !== 0 && Math.abs(n) < 0.0001) return n.toExponential(3);
  // Past 1e15 a double no longer holds every digit, so show scientific notation instead of float noise.
  if (Math.abs(n) >= 1e15) return n.toExponential(4).replace(/\.?0+e/, "e").replace("e+", "e");
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

/** Why a typed size cannot be converted, or "" when it can. */
export function sizeError(value: string): string {
  if (value.trim() === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "Enter a number, like 4.7.";
  if (n < 0) return "Size must be 0 or more.";
  return "";
}
